// ============================================================
// Qatar Timezone Utility — SINGLE SOURCE OF TRUTH
// ALL date range queries and "today" calculations must use these
// ============================================================
export const QATAR_TZ = 'Asia/Qatar';
export const QATAR_OFFSET_MS = 3 * 60 * 60 * 1000; // UTC+3

/** Get today's date string in Qatar timezone: YYYY-MM-DD */
export function getTodayQatar(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: QATAR_TZ });
}

/** Get yesterday's date string in Qatar timezone: YYYY-MM-DD */
export function getYesterdayQatar(): string {
  const d = new Date(Date.now() - 24 * 60 * 60 * 1000);
  return d.toLocaleDateString('en-CA', { timeZone: QATAR_TZ });
}

/**
 * Convert YYYY-MM-DD string to Qatar midnight → next midnight UTC range.
 * Qatar midnight = YYYY-MM-DDT00:00:00+03:00 = previous day 21:00 UTC
 */
export function getQatarDayRange(dateStr: string): { start: string; end: string } {
  const [year, month, day] = dateStr.split('-').map(Number);
  const startUTC = new Date(Date.UTC(year, month - 1, day) - QATAR_OFFSET_MS);
  const endUTC = new Date(startUTC.getTime() + 24 * 60 * 60 * 1000);
  return { start: startUTC.toISOString(), end: endUTC.toISOString() };
}

/**
 * Convert a date range (from/to as Date objects) to Qatar-aligned UTC boundaries.
 * "from" = Qatar midnight of that date, "to" = Qatar end of that date.
 */
export function getQatarRangeFromDates(from: Date, to: Date): { start: string; end: string } {
  const fromStr = from.toLocaleDateString('en-CA', { timeZone: QATAR_TZ });
  const toStr = to.toLocaleDateString('en-CA', { timeZone: QATAR_TZ });
  const { start } = getQatarDayRange(fromStr);
  const { end } = getQatarDayRange(toStr);
  return { start, end };
}

/**
 * startOfDayQatar — returns a Date object at Qatar midnight for a given YYYY-MM-DD string.
 * Drop-in replacement for date-fns startOfDay when used for DB queries.
 */
export function startOfDayQatar(dateStr: string): Date {
  return new Date(getQatarDayRange(dateStr).start);
}

/**
 * endOfDayQatar — returns a Date object at Qatar end-of-day (23:59:59.999) for a given YYYY-MM-DD string.
 */
export function endOfDayQatar(dateStr: string): Date {
  const { end } = getQatarDayRange(dateStr);
  return new Date(new Date(end).getTime() - 1); // 1ms before next midnight
}

/**
 * Get Qatar day range for "today".
 */
export function getTodayQatarRange(): { start: string; end: string } {
  return getQatarDayRange(getTodayQatar());
}

/**
 * Get Qatar day range for "yesterday".
 */
export function getYesterdayQatarRange(): { start: string; end: string } {
  return getQatarDayRange(getYesterdayQatar());
}

/**
 * Get Qatar range for last N days (from N days ago midnight to now).
 */
export function getLastNDaysQatarRange(n: number): { start: string; end: string } {
  const todayStr = getTodayQatar();
  const [y, m, d] = todayStr.split('-').map(Number);
  const fromDate = new Date(Date.UTC(y, m - 1, d - n) - QATAR_OFFSET_MS);
  const { end } = getTodayQatarRange();
  return { start: fromDate.toISOString(), end };
}

/** Format a UTC timestamp for display in Qatar timezone */
export function formatInQatar(utcTimestamp: string, formatStr: 'date' | 'datetime' | 'time' = 'datetime'): string {
  const date = new Date(utcTimestamp);
  if (formatStr === 'date') {
    return date.toLocaleDateString('en-CA', { timeZone: QATAR_TZ });
  }
  if (formatStr === 'time') {
    return date.toLocaleTimeString('en-GB', { timeZone: QATAR_TZ, hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }
  return date.toLocaleString('en-GB', { timeZone: QATAR_TZ });
}

/** Get the Qatar date (YYYY-MM-DD) for a UTC timestamp string */
export function utcToQatarDateStr(utcTimestamp: string): string {
  return new Date(utcTimestamp).toLocaleDateString('en-CA', { timeZone: QATAR_TZ });
}

/**
 * Parse a YYYY-MM-DD string safely without UTC offset.
 * new Date('2026-04-01') → UTC midnight (wrong). This returns local midnight.
 */
export function parseDateStrSafe(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Get Qatar week range (Sunday to Saturday).
 */
export function getQatarWeekRange(weekStartsOn: 0 | 1 = 0): { start: string; end: string } {
  const todayStr = getTodayQatar();
  const [y, m, d] = todayStr.split('-').map(Number);
  const today = new Date(Date.UTC(y, m - 1, d));
  const dayOfWeek = today.getUTCDay();
  const diff = (dayOfWeek - weekStartsOn + 7) % 7;
  const weekStart = new Date(Date.UTC(y, m - 1, d - diff));
  const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
  const startStr = `${weekStart.getUTCFullYear()}-${String(weekStart.getUTCMonth() + 1).padStart(2, '0')}-${String(weekStart.getUTCDate()).padStart(2, '0')}`;
  const endDateObj = new Date(weekEnd.getTime() - 24 * 60 * 60 * 1000);
  const endStr = `${endDateObj.getUTCFullYear()}-${String(endDateObj.getUTCMonth() + 1).padStart(2, '0')}-${String(endDateObj.getUTCDate()).padStart(2, '0')}`;
  return { start: getQatarDayRange(startStr).start, end: getQatarDayRange(endStr).end };
}

/**
 * Get Qatar month range.
 */
export function getQatarMonthRange(): { start: string; end: string } {
  const todayStr = getTodayQatar();
  const [y, m] = todayStr.split('-').map(Number);
  const firstDay = `${y}-${String(m).padStart(2, '0')}-01`;
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const lastDayStr = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { start: getQatarDayRange(firstDay).start, end: getQatarDayRange(lastDayStr).end };
}

/**
 * Get last week's Qatar range.
 */
export function getLastQatarWeekRange(weekStartsOn: 0 | 1 = 0): { start: string; end: string } {
  const todayStr = getTodayQatar();
  const [y, m, d] = todayStr.split('-').map(Number);
  const today = new Date(Date.UTC(y, m - 1, d));
  const dayOfWeek = today.getUTCDay();
  const diff = (dayOfWeek - weekStartsOn + 7) % 7;
  const thisWeekStart = new Date(Date.UTC(y, m - 1, d - diff));
  const lastWeekStart = new Date(thisWeekStart.getTime() - 7 * 24 * 60 * 60 * 1000);
  const lastWeekEnd = new Date(thisWeekStart.getTime() - 24 * 60 * 60 * 1000);
  const startStr = `${lastWeekStart.getUTCFullYear()}-${String(lastWeekStart.getUTCMonth() + 1).padStart(2, '0')}-${String(lastWeekStart.getUTCDate()).padStart(2, '0')}`;
  const endStr = `${lastWeekEnd.getUTCFullYear()}-${String(lastWeekEnd.getUTCMonth() + 1).padStart(2, '0')}-${String(lastWeekEnd.getUTCDate()).padStart(2, '0')}`;
  return { start: getQatarDayRange(startStr).start, end: getQatarDayRange(endStr).end };
}

/**
 * Get last month's Qatar range.
 */
export function getLastQatarMonthRange(): { start: string; end: string } {
  const todayStr = getTodayQatar();
  const [y, m] = todayStr.split('-').map(Number);
  const prevMonth = m === 1 ? 12 : m - 1;
  const prevYear = m === 1 ? y - 1 : y;
  const firstDay = `${prevYear}-${String(prevMonth).padStart(2, '0')}-01`;
  const lastDay = new Date(Date.UTC(prevYear, prevMonth, 0)).getUTCDate();
  const lastDayStr = `${prevYear}-${String(prevMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { start: getQatarDayRange(firstDay).start, end: getQatarDayRange(lastDayStr).end };
}

/**
 * Get a date N days ago in Qatar timezone as YYYY-MM-DD.
 */
export function getNDaysAgoQatar(n: number): string {
  const d = new Date(Date.now() - n * 24 * 60 * 60 * 1000);
  return d.toLocaleDateString('en-CA', { timeZone: QATAR_TZ });
}
